import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Filter, RefreshCw, Download, X } from 'lucide-react';
import CrmStatementDownloadButton from '@/components/statements/CrmStatementDownloadButton';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import {
    Transaction,
    TransactionFilters,
    useCustomerTransactions,
    useUpdateTransaction,
    useDeleteTransaction,
} from '@/api/transactions/use-transactions';
import { formatCurrency, formatDate } from '@/lib/format';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import TransactionModal from './TransactionModal';
import TransactionDetailsModal from './TransactionDetailsModal';
import TransactionFilterDialog from './TransactionFilterDialog';
import GeneralTransactionModal from '../../components/GeneralTransactionModal';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';
import { Link } from 'react-router-dom';

interface TransactionsTabProps {
    customerId: string;
}

const TransactionsTab: React.FC<TransactionsTabProps> = ({ customerId }) => {
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isGeneralTransactionModalOpen, setIsGeneralTransactionModalOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState<TransactionFilters>({
        customer_id: customerId,
        page,
        per_page: pageSize,
    });
    const [activeFiltersCount, setActiveFiltersCount] = useState(0);

    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();
    const updateTransactionMutation = useUpdateTransaction(customerId);
    const deleteTransactionMutation = useDeleteTransaction(customerId);

    const handleViewDetails = (transaction: Transaction) => {
        setSelectedTransaction(transaction);
        setIsDetailsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleCloseDetailsModal = () => {
        setIsDetailsModalOpen(false);
    };

    const handleCreateGeneralTransaction = () => {
        setIsGeneralTransactionModalOpen(true);
    };

    const handleCloseGeneralTransactionModal = () => {
        setIsGeneralTransactionModalOpen(false);
    };

    const handleGeneralTransactionCreated = () => {
        refetch();
        handleCloseGeneralTransactionModal();
    };

    // Apply filters and update the active filters count
    const handleApplyFilters = (newFilters: TransactionFilters) => {
        const filtersWithPage = { ...newFilters, page: 1 };
        setFilters(filtersWithPage);
        setPage(1);

        let count = 0;
        Object.entries(filtersWithPage).forEach(([key, value]) => {
            if (
                value !== undefined &&
                key !== 'page' &&
                key !== 'per_page' &&
                key !== 'customer_id'
            ) {
                count++;
            }
        });
        setActiveFiltersCount(count);
    };

    const handleOpenFilterDialog = () => {
        setIsFilterDialogOpen(true);
    };

    const handleClearFilters = () => {
        const resetFilters: TransactionFilters = {
            customer_id: customerId,
            page: 1,
            per_page: pageSize,
        };
        setFilters(resetFilters);
        setPage(1);
        setActiveFiltersCount(0);
    };

    const {
        data: transactionsData,
        isLoading,
        error,
        refetch,
    } = useCustomerTransactions(customerId, {
        ...filters,
        page,
    });

    const transactions = transactionsData?.data || [];
    const totalPages = transactionsData?.meta?.last_page || 1;
    const totalTransactions = transactionsData?.meta?.total || 0;

    // Show error message if query fails, but don't throw to prevent tab reset
    if (error) {
        console.error('Error loading transactions:', error);
    }

    const getTypeClassName = (type: string) => {
        switch (type) {
            case 'deposit':
                return 'bg-success/10 text-success border-success/20';
            case 'withdrawal':
            case 'withdraw':
                return 'bg-destructive/10 text-destructive border-destructive/20';
            case 'bonus':
                return 'bg-warning/10 text-warning border-warning/20';
            default:
                return 'bg-muted text-muted-foreground border-border';
        }
    };

    const getCreatedByLabel = (transaction: Transaction) => {
        const tx = transaction as Transaction & {
            created_by_type?: string;
            created_by_name?: string;
            metadata?: Record<string, any>;
        };

        if (tx.created_by_type === 'customer') {
            return 'customer';
        }

        if (tx.created_by_type === 'crm_user') {
            return tx.created_by_name || 'CRM User';
        }

        const source = tx.metadata?.source;
        if (source && source !== 'crm_manual') {
            return 'customer';
        }

        return tx.created_by_name || 'CRM User';
    };

    const handleApproveTransaction = async (transactionId: string) => {
        try {
            await updateTransactionMutation.mutateAsync({
                transactionId,
                data: { status: 'completed' },
            });
            enqueueSnackbar('Transaction approved successfully', { variant: 'success' });
            refetch();
            handleCloseDetailsModal();
        } catch (error) {
            enqueueSnackbar('Failed to approve transaction', { variant: 'error' });
        }
    };

    const handleRejectTransaction = async (transactionId: string, reason: string) => {
        try {
            await updateTransactionMutation.mutateAsync({
                transactionId,
                data: { status: 'cancelled', rejection_reason: reason },
            });
            enqueueSnackbar('Transaction rejected successfully', { variant: 'success' });
            refetch();
            handleCloseDetailsModal();
        } catch (error) {
            enqueueSnackbar('Failed to reject transaction', { variant: 'error' });
        }
    };

    const handleDeleteTransaction = async (transactionId: string) => {
        try {
            await deleteTransactionMutation.mutateAsync(transactionId);
            enqueueSnackbar('Transaction deleted successfully', { variant: 'success' });
            refetch();
            handleCloseDetailsModal();
        } catch (error) {
            enqueueSnackbar('Failed to delete transaction', { variant: 'error' });
        }
    };

    const handleUpdateNotes = async (transactionId: string, notes: string) => {
        try {
            await updateTransactionMutation.mutateAsync({
                transactionId,
                data: { notes },
            });
            enqueueSnackbar('Notes saved', { variant: 'success' });
            refetch();
        } catch (error) {
            enqueueSnackbar('Failed to save notes', { variant: 'error' });
        }
    };

    const handleExportToCsv = () => {
        if (transactions.length === 0) {
            enqueueSnackbar('No transaction data to export', {
                variant: 'warning',
                autoHideDuration: 3000,
            });
            return;
        }

        try {
            const headers = [
                'Date & Time',
                'Type',
                'Amount',
                'Currency',
                'Status',
                'Payment Method',
                'Reference Number',
                'Description',
                'Notes',
            ];

            const escapeCSV = (value: any): string => {
                const stringValue = String(value);
                if (
                    stringValue.includes(',') ||
                    stringValue.includes('"') ||
                    stringValue.includes('\n')
                ) {
                    return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
            };

            const csvRows = transactions.map(transaction => {
                const row = [
                    format(new Date(transaction.created_at), 'yyyy-MM-dd HH:mm:ss'),
                    transaction.type.toUpperCase(),
                    transaction.amount,
                    transaction.currency,
                    transaction.status.toUpperCase(),
                    transaction.payment_method || 'N/A',
                    transaction.reference_number || 'N/A',
                    transaction.description || 'N/A',
                    transaction.notes || 'N/A',
                ];

                return row.map(escapeCSV).join(',');
            });

            const csvContent = [headers.map(escapeCSV).join(','), ...csvRows].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);

            const filename = `transactions-customer-${customerId}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
            link.setAttribute('download', filename);

            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            enqueueSnackbar(`Transactions exported successfully as ${filename}`, {
                variant: 'success',
                autoHideDuration: 3000,
            });
        } catch (error) {
            console.error('Error exporting transactions:', error);
            enqueueSnackbar('Failed to export transactions. Please try again.', {
                variant: 'error',
                autoHideDuration: 5000,
            });
        }
    };

    const columns: Column<Transaction>[] = useMemo(
        () => [
            {
                id: 'created_at',
                label: 'Date & Time',
                key: 'created_at',
                width: '160px',
                render: (_value, transaction) => formatDate(transaction.created_at, 'datetime'),
            },
            {
                id: 'type',
                label: 'Type',
                key: 'type',
                width: '110px',
                render: (_value, transaction) => (
                    <Badge variant="outline" className={getTypeClassName(transaction.type)}>
                        {transaction.type.toUpperCase()}
                    </Badge>
                ),
            },
            {
                id: 'customer',
                label: 'Customer',
                key: 'customer',
                width: '160px',
                render: (_value, transaction) =>
                    transaction.customer?.id ? (
                        <Link
                            to={`/customers/${transaction.customer.id}`}
                            className="text-primary hover:underline"
                            onClick={event => event.stopPropagation()}
                        >
                            {transaction.customer?.name || 'N/A'}
                        </Link>
                    ) : (
                        transaction.customer?.name || 'N/A'
                    ),
            },
            {
                id: 'amount',
                label: 'Amount',
                key: 'amount',
                width: '120px',
                render: (_value, transaction) => (
                    <span className="font-medium text-right tabular-figures">
                        {formatCurrency(transaction.amount, transaction.currency)}
                    </span>
                ),
            },
            {
                id: 'status',
                label: 'Status',
                key: 'status',
                width: '110px',
                render: (_value, transaction) => (
                    <StatusBadge status={transaction.status} kind="transaction" />
                ),
            },
            {
                id: 'payment_method',
                label: 'Payment Method',
                key: 'payment_method',
                width: '140px',
                render: (_value, transaction) => transaction.payment_method || 'N/A',
            },
            {
                id: 'created_by',
                label: 'Created By',
                key: 'id',
                sortable: false,
                width: '140px',
                render: (_value, transaction) => getCreatedByLabel(transaction),
            },
            {
                id: 'reference_number',
                label: 'Reference',
                key: 'reference_number',
                width: '140px',
                render: (_value, transaction) => transaction.reference_number || 'N/A',
            },
        ],
        []
    );

    return (
        <div className="space-y-4">
            {/* Transaction Modal */}
            <TransactionModal
                open={isModalOpen}
                onClose={handleCloseModal}
                customerId={customerId}
                onSuccess={() => {
                    refetch();
                    handleCloseModal();
                }}
            />

            {/* Transaction Details Modal: open on row click; actions in footer by status & permissions */}
            <TransactionDetailsModal
                open={isDetailsModalOpen}
                onClose={handleCloseDetailsModal}
                transaction={selectedTransaction}
                canEdit={hasPermission(ModulePermission.TRANSACTIONS, PermissionAction.EDIT)}
                canDelete={hasPermission(ModulePermission.TRANSACTIONS, PermissionAction.DELETE)}
                onApprove={handleApproveTransaction}
                onReject={handleRejectTransaction}
                onDelete={handleDeleteTransaction}
                onUpdateNotes={handleUpdateNotes}
            />

            {/* Filter Dialog */}
            <TransactionFilterDialog
                isOpen={isFilterDialogOpen}
                onClose={() => setIsFilterDialogOpen(false)}
                filters={filters}
                onApplyFilters={handleApplyFilters}
            />

            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">
                    Deposits & Withdrawals {totalTransactions > 0 && `(${totalTransactions})`}
                </h2>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={() => {
                            setIsRefreshing(true);
                            refetch().finally(() => {
                                setIsRefreshing(false);
                            });
                        }}
                        disabled={isLoading || isRefreshing}
                    >
                        <RefreshCw
                            className={`h-4 w-4 ${isLoading || isRefreshing ? 'animate-spin' : ''}`}
                        />
                        Refresh
                    </Button>
                    <div className="relative">
                        <Button
                            variant={activeFiltersCount > 0 ? 'default' : 'outline'}
                            className="flex items-center gap-2"
                            onClick={handleOpenFilterDialog}
                        >
                            <Filter className="h-4 w-4" />
                            Filter {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                        </Button>
                        {activeFiltersCount > 0 && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute -right-2 -top-2 h-5 w-5 rounded-full bg-primary text-primary-foreground"
                                onClick={handleClearFilters}
                            >
                                <X className="h-3 w-3" />
                                <span className="sr-only">Clear filters</span>
                            </Button>
                        )}
                    </div>
                    <Button
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={handleExportToCsv}
                        disabled={isLoading || isRefreshing || transactions.length === 0}
                    >
                        <Download className="h-4 w-4" />
                        Export
                    </Button>
                    <CrmStatementDownloadButton customerId={customerId} variant="desktop" />
                    {hasPermission(ModulePermission.TRANSACTIONS, PermissionAction.CREATE) && (
                        <Button
                            className="flex items-center gap-2"
                            onClick={handleCreateGeneralTransaction}
                        >
                            <Plus className="h-4 w-4" />
                            Create Transaction
                        </Button>
                    )}
                </div>
            </div>

            {error && (
                <p className="text-sm text-destructive">
                    Error loading transactions. Please try again.
                </p>
            )}

            <GenericDataTable<Transaction>
                data={transactions}
                columns={columns}
                visibleColumns={columns.map(col => col.id)}
                filters={{}}
                filterFields={[]}
                presets={[]}
                isLoading={isLoading || isRefreshing}
                selectedItems={[]}
                onSelectionChange={() => {}}
                getItemId={transaction => transaction.id}
                sortConfig={{ key: 'created_at', direction: 'desc' }}
                onSort={() => {}}
                totalItems={totalTransactions}
                totalPages={totalPages}
                currentPage={page}
                pageSize={pageSize}
                onPageChange={setPage}
                showExportButton={false}
                onRowClick={handleViewDetails}
                onFilterChange={() => {}}
                onClearFilters={() => {}}
                onColumnToggle={() => {}}
                onResetColumns={() => {}}
                emptyMessage="No transactions found"
                loadingMessage="Loading transactions…"
                emptyState={
                    <EmptyState
                        title="No transactions found"
                        description="Deposits, withdrawals and bonuses will appear here once recorded."
                        action={
                            hasPermission(
                                ModulePermission.TRANSACTIONS,
                                PermissionAction.CREATE
                            ) ? (
                                <Button size="sm" onClick={() => setIsModalOpen(true)}>
                                    <Plus className="h-4 w-4" />
                                    Add First Transaction
                                </Button>
                            ) : undefined
                        }
                    />
                }
            />

            {/* General Transaction Modal */}
            <GeneralTransactionModal
                open={isGeneralTransactionModalOpen}
                onClose={handleCloseGeneralTransactionModal}
                onSuccess={handleGeneralTransactionCreated}
                customerId={customerId}
            />
        </div>
    );
};

export default TransactionsTab;
